import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  Archive,
  Building2,
  CalendarClock,
  CalendarRange,
  FolderKanban,
  FolderPlus,
  SearchX,
  Users,
  Wallet,
  X,
} from "lucide-react";
import {
  businessDate,
  money,
  PROJECT_STATUSES,
  PROJECT_STATUS_LABEL,
  seesMoney,
  type ClientRow,
  type ProjectRow,
  type ProjectStatus,
} from "@contractorhub/shared";
import { adminClientsQuery, adminContractorsQuery } from "../../../lib/queries";
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  SearchInput,
  Select,
  StatCard,
  Tabs,
  cn,
} from "../../../components/ui";
import { NewClientDialog } from "../../../components/new-client-dialog";
import { NewProjectDialog } from "../../../components/new-project-dialog";
import { RecordExpenseButton } from "../../../components/expenses/record-expense-modal";
import { ClientCard } from "../../../components/clients/client-card";
import { ProjectsTable } from "../../../components/clients/projects-table";
import {
  ENDING_SOON_DAYS,
  compactMoney,
  isEndingSoon,
} from "../../../components/clients/project-bits";

const searchSchema = z.object({
  view: z.enum(["clients", "projects"]).default("clients"),
  /** Which clients are in scope — and, in the Projects view, whose projects. */
  tab: z.enum(["active", "archived"]).default("active"),
  q: z.string().optional(),
  client: z.string().optional(),
  pstatus: z.enum(PROJECT_STATUSES).optional(),
  ending: z.boolean().optional(),
});

type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_authed/_staff/clients")({
  validateSearch: searchSchema,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminClientsQuery),
      context.queryClient.ensureQueryData(adminContractorsQuery),
    ]),
  component: ClientsPage,
});

/** What a client card shows once the search has had its say. */
type ClientMatch = {
  client: ClientRow;
  /** The projects to render — all of them, or only those that matched. */
  projects: ProjectRow[];
  /** True when the search narrowed the project list, so the card can say so. */
  narrowed: boolean;
};

function includesNeedle(value: string | null | undefined, needle: string): boolean {
  return (value ?? "").toLowerCase().includes(needle);
}

/**
 * Search, over clients and their projects at once.
 *
 * A client that matches by its own name keeps its whole project list — you asked
 * for the client, not for a subset of its work. A client that matched only
 * because one of its projects did shows just those projects, because the reason
 * it is on screen is the thing you should be looking at.
 */
function matchClients(clients: ClientRow[], needle: string): ClientMatch[] {
  if (!needle) {
    return clients.map((client) => ({
      client,
      projects: client.projects,
      narrowed: false,
    }));
  }

  const matches: ClientMatch[] = [];
  for (const client of clients) {
    const clientMatched =
      includesNeedle(client.name, needle) ||
      includesNeedle(client.shortCode, needle) ||
      includesNeedle(client.primaryContactName, needle) ||
      includesNeedle(client.primaryContactEmail, needle);

    if (clientMatched) {
      matches.push({ client, projects: client.projects, narrowed: false });
      continue;
    }

    const projects = client.projects.filter(
      (project) =>
        includesNeedle(project.name, needle) || includesNeedle(project.code, needle)
    );
    if (projects.length > 0) matches.push({ client, projects, narrowed: true });
  }
  return matches;
}

function ClientsPage() {
  const search = Route.useSearch();
  const { view, tab, q, client: clientFilter, pstatus, ending } = search;
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();
  // A user admin manages clients and projects but sees no money (AUTH-13): the
  // API has already left budget and spend out, and this hides where they sat.
  const fullAdmin = seesMoney(user.role);
  const { data } = useSuspenseQuery(adminClientsQuery);
  const { data: contractorData } = useSuspenseQuery(adminContractorsQuery);
  // The business's day, resolved once so every row answers against the same one.
  const today = useMemo(() => businessDate(), []);

  const needle = (q ?? "").trim().toLowerCase();
  const searching = needle.length > 0;

  const model = useMemo(() => {
    const active = data.clients.filter((c) => c.status === "ACTIVE");
    const archived = data.clients.filter((c) => c.status !== "ACTIVE");
    const liveClientIds = new Set(active.map((c) => c.id));
    const liveProjects = active.flatMap((c) => c.projects);
    const projectStatus = new Map(
      data.clients.flatMap((c) => c.projects).map((p) => [p.id, p.status])
    );

    // Distinct people, not assignment rows: one contractor on two projects of
    // the same client is one contractor to that client.
    const contractorsByClient = new Map<string, Set<string>>();
    const liveContractors = new Set<string>();
    for (const contractor of contractorData.contractors) {
      for (const a of contractor.assignments) {
        if (!a.active || projectStatus.get(a.projectId) === "CLOSED") continue;
        const set = contractorsByClient.get(a.clientId) ?? new Set<string>();
        set.add(contractor.id);
        contractorsByClient.set(a.clientId, set);
        if (liveClientIds.has(a.clientId)) liveContractors.add(contractor.id);
      }
    }

    const budgeted = liveProjects.filter(
      (p) => p.status !== "CLOSED" && p.budget != null && p.budget > 0
    );
    return {
      active,
      archived,
      contractorsByClient,
      kpi: {
        clients: active.length,
        archivedClients: archived.length,
        activeProjects: liveProjects.filter((p) => p.status === "ACTIVE").length,
        onHold: liveProjects.filter((p) => p.status === "ON_HOLD").length,
        totalProjects: liveProjects.length,
        contractors: liveContractors.size,
        budget: budgeted.reduce((n, p) => n + (p.budget ?? 0), 0),
        spend: budgeted.reduce((n, p) => n + (p.approvedSpend ?? 0), 0),
        endingSoon: liveProjects.filter((p) => isEndingSoon(p, today)).length,
      },
    };
  }, [data.clients, contractorData.contractors, today]);

  const scopeClients = tab === "archived" ? model.archived : model.active;
  const clientMatches = useMemo(
    () => matchClients(scopeClients, needle),
    [scopeClients, needle]
  );
  const otherScopeMatches = useMemo(
    () => matchClients(tab === "archived" ? model.active : model.archived, needle),
    [tab, model, needle]
  );

  const scopeProjects = useMemo(
    () => scopeClients.flatMap((c) => c.projects),
    [scopeClients]
  );
  const projectMatches = useMemo(
    () =>
      scopeProjects.filter(
        (p) =>
          (!clientFilter || p.clientId === clientFilter) &&
          (!pstatus || p.status === pstatus) &&
          (!ending || isEndingSoon(p, today)) &&
          (!needle ||
            includesNeedle(p.name, needle) ||
            includesNeedle(p.code, needle) ||
            includesNeedle(p.clientName, needle))
      ),
    [scopeProjects, clientFilter, pstatus, ending, needle, today]
  );

  // Typing replaces, switching views pushes. Without the first, the back button
  // walks the user back through their own search term one letter at a time;
  // without the second, it cannot undo the view they just left.
  const patch = (next: Partial<Search>, replace = false) =>
    void navigate({ to: "/clients", search: { ...search, ...next }, replace });

  const projectFiltersOn = Boolean(clientFilter || pstatus || ending || searching);
  const clearProjectFilters = () =>
    patch({ client: undefined, pstatus: undefined, ending: undefined, q: undefined }, true);

  const { kpi } = model;
  const burnPct = kpi.budget > 0 ? Math.round((kpi.spend / kpi.budget) * 100) : null;

  return (
    <div>
      <PageHeader
        title="Clients & Projects"
        description={`${kpi.clients} ${kpi.clients === 1 ? "client" : "clients"} · ${kpi.activeProjects} active ${kpi.activeProjects === 1 ? "project" : "projects"} · ${kpi.contractors} ${kpi.contractors === 1 ? "contractor" : "contractors"} engaged`}
        actions={
          <>
            {/* Archived clients are kept out of the picker, which is exactly
                what the Archive button in Edit · client promises it does. */}
            {fullAdmin && <RecordExpenseButton />}
            <NewProjectDialog
              clients={model.active}
              contractors={contractorData.contractors}
              showBudget={fullAdmin}
            />
            <NewClientDialog />
          </>
        }
      />

      <div
        className={cn(
          "mb-6 grid grid-cols-2 gap-4",
          fullAdmin
            ? "md:grid-cols-3 xl:grid-cols-5 [&>*:last-child]:col-span-2 md:[&>*:last-child]:col-span-1"
            : "md:grid-cols-4"
        )}
      >
        <StatCard
          label="Active clients"
          value={kpi.clients}
          icon={Building2}
          hint={kpi.archivedClients > 0 ? `${kpi.archivedClients} archived` : "None archived"}
        />
        <StatCard
          label="Active projects"
          value={kpi.activeProjects}
          icon={FolderKanban}
          hint={kpi.onHold > 0 ? `${kpi.onHold} on hold` : `${kpi.totalProjects} in total`}
        />
        <StatCard
          label="Contractors"
          value={kpi.contractors}
          icon={Users}
          tone="neutral"
          hint="On live projects"
        />
        {fullAdmin && (
          <StatCard
            label="Budget used"
            value={burnPct == null ? "—" : `${burnPct}%`}
            icon={Wallet}
            tone={burnPct != null && burnPct >= 100 ? "danger" : burnPct != null && burnPct >= 80 ? "warning" : "success"}
            hint={
              kpi.budget > 0
                ? `${compactMoney(kpi.spend)} of ${compactMoney(kpi.budget)}`
                : "No project budgets set"
            }
          />
        )}
        <StatCard
          label="Ending soon"
          value={kpi.endingSoon}
          icon={CalendarClock}
          tone={kpi.endingSoon > 0 ? "warning" : "neutral"}
          hint={`Within ${ENDING_SOON_DAYS} days`}
          to="/clients"
          search={{ view: "projects", tab: "active", ending: true }}
        />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <Tabs
          items={[
            { value: "clients", label: "Clients", count: clientMatches.length },
            { value: "projects", label: "Projects", count: projectMatches.length },
          ]}
          value={view}
          onChange={(next) => patch({ view: next })}
          className="flex-1"
        />
        <div className="flex items-center border-b border-line pb-1.5 sm:pb-0">
          <ScopeToggle
            value={tab}
            onChange={(next) => patch({ tab: next, client: undefined })}
            counts={{ active: model.active.length, archived: model.archived.length }}
          />
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="lg:w-80">
          <SearchInput
            value={q ?? ""}
            onChange={(next) => patch({ q: next || undefined }, true)}
            placeholder={
              view === "clients" ? "Search clients, contacts or projects" : "Search projects, codes or clients"
            }
            aria-label="Search clients or projects"
          />
        </div>
        {view === "projects" && (
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <Select
              aria-label="Filter by client"
              className="sm:w-56"
              value={clientFilter ?? ""}
              onChange={(event) => patch({ client: event.target.value || undefined }, true)}
            >
              <option value="">All clients</option>
              {scopeClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Filter by project status"
              className="sm:w-40"
              value={pstatus ?? ""}
              onChange={(event) =>
                patch({ pstatus: (event.target.value || undefined) as ProjectStatus | undefined }, true)
              }
            >
              <option value="">All statuses</option>
              {PROJECT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {PROJECT_STATUS_LABEL[status]}
                </option>
              ))}
            </Select>
            <button
              type="button"
              aria-pressed={Boolean(ending)}
              onClick={() => patch({ ending: ending ? undefined : true }, true)}
              className={cn(
                "inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-sm transition-colors duration-150",
                ending
                  ? "border-warning-soft-border bg-warning-soft font-medium text-warning-on-soft"
                  : "border-line bg-surface text-muted hover:bg-surface-hover hover:text-text"
              )}
            >
              <CalendarRange className="h-4 w-4" aria-hidden="true" />
              Ending soon
            </button>
            {projectFiltersOn && (
              <Button variant="ghost" size="sm" icon={X} onClick={clearProjectFilters}>
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {view === "clients" ? (
        clientMatches.length === 0 ? (
          <Card>
            <EmptyClients
              tab={tab}
              query={q ?? ""}
              searching={searching}
              otherTabCount={otherScopeMatches.length}
              onSwitchTab={() => patch({ tab: tab === "archived" ? "active" : "archived" })}
              onClearSearch={() => patch({ q: undefined }, true)}
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {clientMatches.map((match) => (
              <ClientCard
                key={match.client.id}
                client={match.client}
                projects={match.projects}
                narrowed={match.narrowed}
                contractorCount={model.contractorsByClient.get(match.client.id)?.size ?? 0}
                contractors={contractorData.contractors}
                today={today}
                onOpenProjects={(clientId) =>
                  patch({ view: "projects", client: clientId, q: undefined })
                }
                showMoney={fullAdmin}
              />
            ))}
          </div>
        )
      ) : (
        <Card className="overflow-hidden">
          <ProjectsTable
            projects={projectMatches}
            today={today}
            showMoney={fullAdmin}
            empty={
              projectFiltersOn ? (
                <EmptyState
                  icon={SearchX}
                  title="No matching projects"
                  description="Nothing matches the current search and filters."
                  action={
                    <Button variant="secondary" icon={X} onClick={clearProjectFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={tab === "archived" ? Archive : FolderPlus}
                  title={tab === "archived" ? "No archived projects" : "No projects yet"}
                  description={
                    tab === "archived"
                      ? "Projects belonging to archived clients appear here."
                      : "Add a project to a client to start assigning contractors."
                  }
                />
              )
            }
          />
          {projectMatches.length > 0 && (
            <ProjectsFooter projects={projectMatches} showMoney={fullAdmin} />
          )}
        </Card>
      )}
    </div>
  );
}

/** Totals for exactly the rows on screen, so a filter reads as a sum. */
function ProjectsFooter({
  projects,
  showMoney,
}: {
  projects: ProjectRow[];
  showMoney: boolean;
}) {
  const budget = projects.reduce((n, p) => n + (p.budget ?? 0), 0);
  const spend = projects.reduce((n, p) => n + (p.approvedSpend ?? 0), 0);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-surface-sunken/60 px-5 py-3 text-xs text-subtle">
      <span>
        {projects.length} {projects.length === 1 ? "project" : "projects"}
      </span>
      {showMoney && (
        <span className="tabular-nums">
          {money(spend)} approved{budget > 0 ? ` of ${money(budget)} budgeted` : ""}
        </span>
      )}
    </div>
  );
}

/** Active / Archived, as a segmented control: it scopes, it does not navigate. */
function ScopeToggle({
  value,
  onChange,
  counts,
}: {
  value: "active" | "archived";
  onChange: (next: "active" | "archived") => void;
  counts: { active: number; archived: number };
}) {
  const options = [
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
  ] as const;
  return (
    <div
      role="radiogroup"
      aria-label="Client status"
      className="inline-flex rounded-lg border border-line bg-surface-sunken p-0.5"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm transition-all duration-150",
              selected
                ? "bg-surface font-medium text-text ring-1 ring-line-strong"
                : "text-subtle hover:text-text"
            )}
          >
            {option.label}
            <span className="text-micro tabular-nums text-subtle">{counts[option.value]}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The empty state, which has four different things to say.
 *
 * "No clients yet" and "nothing matched your search" are opposite situations and
 * used to be told apart nowhere. When a search comes up dry in one tab but has
 * hits in the other, the way out is a button, not a hunt.
 */
function EmptyClients({
  tab,
  query,
  searching,
  otherTabCount,
  onSwitchTab,
  onClearSearch,
}: {
  tab: "active" | "archived";
  query: string;
  searching: boolean;
  otherTabCount: number;
  onSwitchTab: () => void;
  onClearSearch: () => void;
}) {
  if (!searching) {
    return tab === "archived" ? (
      <EmptyState
        icon={Archive}
        title="Nothing archived"
        description="Archiving a client hides it from here and from the pickers. Its projects and timesheets stay put."
      />
    ) : (
      <EmptyState
        icon={Building2}
        title="No clients yet"
        description="Add a client, then give it a project."
      />
    );
  }

  const otherTab = tab === "archived" ? "Active" : "Archived";
  return (
    <EmptyState
      icon={SearchX}
      title="No matches"
      description={`Nothing in ${tab === "archived" ? "Archived" : "Active"} matches “${query.trim()}”.`}
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          {otherTabCount > 0 && (
            <Button variant="secondary" onClick={onSwitchTab}>
              {otherTabCount} in {otherTab}
            </Button>
          )}
          <Button variant="ghost" onClick={onClearSearch}>
            Clear search
          </Button>
        </div>
      }
    />
  );
}
